// Unified order-level permission resolution for the Order Details page.
// See memory "LTL checklist permissions" for the authoritative matrix.
//   - Onward / Corp admins not impersonating: full access.
//   - Shipper: full write on order.oms=true, read-only on oms=false (oms-true
//     wins over any carrier-assigned / in-progress lockout).
//   - Carrier: full iff viewer owns the order (carrier_id or warehouse_carrier_id).
//   - Teammate: inherits parent client's access; write requires teammate
//     `admin` or `operations` role. Manifest editing additionally allows `warehouse`.
//   - An admin currently impersonating a client is not an admin for this
//     check (isOnwardAdmin / isCorporateAdmin guard on !client.client_id).
//
// Returns three flags:
//   - editable:          order-level write (checklist, status, cancel, notes, edit, reschedule).
//   - manifestEditable:  editable, plus warehouse teammates.
//   - documentsViewable: can see/download the BOL + item-label popover. A superset of editable:
//     also true for a shipper (or an admin impersonating them) on their own order regardless of
//     oms, where they're otherwise read-only. Shipper teammates need an `admin` or `viewer` role.
export const computeOrderPermissions = ({
    order,
    user_id,
    userType,
    teammateRoles,
    accountType,
    isOnwardAdmin,
    isCorporateAdmin,
}) => {
    // 'failed' is a terminal attempt-billing artifact: lock it down entirely the same way read_only is.
    if (order?.read_only || order?.order_status === 'failed') {
        return { editable: false, manifestEditable: false, documentsViewable: false };
    }

    const adminEditable = isOnwardAdmin || isCorporateAdmin;
    const carrierEditable =
        accountType === 'carrier' && (user_id === order.carrier_id || user_id === order.warehouse_carrier_id);
    const shipperEditable = accountType === 'shipper' && user_id === order.shipper_id && !!order.oms;
    const clientEditable = carrierEditable || shipperEditable;

    const teammateEditable = userType !== 'teammate' || !!teammateRoles?.admin || !!teammateRoles?.operations;
    const warehouseTeammateEditable =
        userType !== 'teammate' || !!teammateRoles?.admin || !!teammateRoles?.operations || !!teammateRoles?.warehouse;

    // Document access is read-only, so it allows the owning shipper even on oms=false; teammates need admin/viewer.
    const teammateDocViewer = userType !== 'teammate' || !!teammateRoles?.admin || !!teammateRoles?.viewer;
    const shipperViewer = accountType === 'shipper' && user_id === order.shipper_id && teammateDocViewer;

    const editable = adminEditable || (teammateEditable && clientEditable);

    return {
        editable,
        manifestEditable: adminEditable || (warehouseTeammateEditable && clientEditable),
        documentsViewable: editable || shipperViewer,
    };
};
